import * as Sentry from "@sentry/react-native";

class SessionTokenStorage {
  private key: string;
  private inMemoryToken: string | null;

  /**
   * @param key storage key for sessionStorage
   */
  constructor() {
    this.key = "sessionToken";
    this.inMemoryToken = null;
  }

  private _getCookie = (): string | null => {
    const nameEQ = `${encodeURIComponent(this.key)}=`;
    const parts = document.cookie.split(";").map((c) => c.trim());

    for (const part of parts) {
      if (part.startsWith(nameEQ)) {
        return decodeURIComponent(part.substring(nameEQ.length));
      }
    }
    return null;
  };

  /**
   * Store the token in memory & sessionStorage
   * @param token  the auth token
   */
  public setToken = (token: string): void => {
    console.log("%cSetting token", tokenFromLogStyle);
    this.inMemoryToken = token;

    // Write sessionStorage as secondary persistence
    try {
      window.sessionStorage.setItem(this.key, token);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          type: "sessionStorage_error",
        },
      });
    }
  };

  /**
   * Retrieve the token, checking in-memory → sessionStorage → legacy methods
   * Caches the result in memory.
   */
  public getToken = (): string | null => {
    // 1) in‐memory
    if (this.inMemoryToken) {
      console.log("%cUsing in memory token", tokenFromLogStyle);
      return this.inMemoryToken;
    }

    // 2) sessionStorage
    try {
      const ss = window.sessionStorage.getItem(this.key);
      if (ss) {
        console.log("%cUsing sessionStorage token", tokenFromLogStyle);
        this.inMemoryToken = ss;
        return ss;
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          type: "sessionStorage_error",
        },
      });
    }

    // Legacy. Can remove in September 2025
    // 3) secure cookie
    const ck = this._getCookie();
    if (ck) {
      console.log("%cLegacy: Using cookie token", tokenFromLogStyle);
      this.setToken(ck);
      // Expire cookie after saving it in memory + sessionStorage
      document.cookie = [`${encodeURIComponent(this.key)}=`, "expires=Thu, 01 Jan 1970 00:00:00 GMT", "path=/", "Secure", "SameSite=Strict"].join("; ");
      return ck;
    }

    // 4) localStorage
    try {
      const ls = window.localStorage.getItem(this.key);
      if (ls) {
        console.log("%cLegacy: Using localStorage token", tokenFromLogStyle);
        this.setToken(ls);
        // Remove from localStorage after saving it in memory + sessionStorage
        window.localStorage.removeItem(this.key);
        return ls;
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          type: "localStorage_error",
        },
      });
    }

    console.log("%cNo token in memory, sessionStorage or legacy methods", tokenFromLogStyle);
    return null;
  };

  /** Remove the token from memory & sessionStorage */
  public removeToken = (): void => {
    this.inMemoryToken = null;

    try {
      window.sessionStorage.removeItem(this.key);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          type: "sessionStorage_error",
        },
      });
    }
  };
}

const tokenFromLogStyle = ["color: #0EA5E9", "font-weight: bold"].join(";");

const sessionTokenStorage = new SessionTokenStorage();

export default sessionTokenStorage;
